// Feature Documentation: docs/features/global-case-search.md
import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, 
  Heading, 
  Text, 
  Table, 
  Thead, 
  Tbody, 
  Tr, 
  Th, 
  Td, 
  Spinner, 
  Alert, 
  AlertIcon,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Link,
  Input,
  Button,
  HStack,
  InputGroup,
  InputRightElement,
  CloseButton
} from '@chakra-ui/react';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import { AppContext } from './AppContext';
import HeaderMenus from './HeaderMenus';
import { useFeatureFlags } from './FeatureFlags';

const GlobalSearchResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { accessToken } = useContext(AppContext);
  const { featureFlags } = useFeatureFlags();
  
  // Read query from router state to keep PHI out of the URL
  const initialQuery = location.state?.query || '';
  
  const [activeQuery, setActiveQuery] = useState(initialQuery);
  const [searchInput, setSearchInput] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sync with location state if it changes (e.g., user searches from header while on this page)
  useEffect(() => {
    if (location.state?.query !== undefined) {
      setActiveQuery(location.state.query);
      setSearchInput(location.state.query);
    }
  }, [location.state?.query]);

  useEffect(() => {
    const fetchResults = async () => {
      if (!accessToken) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const limit = activeQuery ? 50 : 30;
        const response = await fetch(`${window.env.REACT_APP_REVEDY_API_HOST}/api/search/cases`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ query: activeQuery, limit })
        });

        if (!response.ok) {
          throw new Error('Failed to fetch search results');
        }

        const data = await response.json();
        setResults(data.cases || []);
      } catch (err) {
        console.error("Search error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [activeQuery, accessToken]);

  const handleSearch = (e) => {
    e.preventDefault();
    setActiveQuery(searchInput);
    // Update location state so a refresh keeps the query
    navigate('/search', { state: { query: searchInput }, replace: true });
  };

  const handleClear = () => {
    setSearchInput('');
    setActiveQuery('');
    navigate('/search', { state: { query: '' }, replace: true });
  };

  // Determine where to route the user when they click a case
  const getCaseRoute = (caseItem) => {
    const orgName = featureFlags?.menu_version?.toLowerCase() || '';
    const externalId = caseItem.external_case_id || caseItem.id;
    
    const isMPH = orgName.includes('mpower') || orgName === 'mph';
    
    if (isMPH) {
      return `/mph-surgical-assist/${externalId}`;
    }
    
    // Default to Medical Necessity Analysis
    return `/ionm-strict-analysis/${externalId}`;
  };
  
  const isNMA = featureFlags?.menu_version?.toLowerCase() === 'nma' || 
                (featureFlags?.menu_version?.toLowerCase() || '').includes('revedy');

  const getStatusBadge = (status) => {
    if (!status) return null;
    
    const statusColors = {
      'complete': 'green',
      'completed': 'green',
      'in_progress': 'orange',
      'pending': 'yellow',
      'failed': 'red',
      'not_started': 'gray'
    };
    
    const color = statusColors[status.toLowerCase()] || 'gray';
    return <Badge colorScheme={color}>{status.replace('_', ' ')}</Badge>;
  };

  return (
    <Box h="100vh" display="flex" flexDirection="column">
      <HeaderMenus />
      
      <Box p={8} flex="1" overflowY="auto" bg="gray.50">
        <Card maxW="1200px" mx="auto" shadow="md">
          <CardHeader bg="white" borderBottomWidth="1px" pb={4}>
            <Heading size="lg" mb={4}>Case Search</Heading>
            
            <form onSubmit={handleSearch}>
              <HStack mb={4}>
                <InputGroup size="md">
                  <Input
                    placeholder="Search by Case ID, Patient ID, or Description..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    bg="white"
                  />
                  {searchInput && (
                    <InputRightElement>
                      <CloseButton size="sm" onClick={handleClear} />
                    </InputRightElement>
                  )}
                </InputGroup>
                <Button type="submit" colorScheme="blue" px={8}>
                  Search
                </Button>
              </HStack>
            </form>

            <Text color="gray.600">
              {activeQuery ? (
                <>Showing results for: <strong>"{activeQuery}"</strong></>
              ) : (
                <>Showing recent cases</>
              )}
            </Text>
          </CardHeader>
          
          <CardBody>
            {loading ? (
              <Box textAlign="center" py={10}>
                <Spinner size="xl" color="blue.500" />
                <Text mt={4}>Searching cases...</Text>
              </Box>
            ) : error ? (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {error}
              </Alert>
            ) : results.length === 0 ? (
              <Box textAlign="center" py={10}>
                <Text fontSize="lg" color="gray.500">No cases found matching your search.</Text>
              </Box>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Case ID</Th>
                      <Th>Patient/Encounter ID</Th>
                      <Th>Description</Th>
                      <Th>Type</Th>
                      <Th>Status</Th>
                      <Th>Updated</Th>
                      {isNMA && <Th>Legacy View</Th>}
                      {isNMA && <Th>Claim Analysis</Th>}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {results.map((caseItem) => (
                      <Tr key={caseItem.id} _hover={{ bg: "gray.50" }}>
                        <Td>
                          <Link 
                            as={RouterLink} 
                            to={getCaseRoute(caseItem)}
                            color="blue.600"
                            fontWeight="bold"
                          >
                            {caseItem.external_case_id || caseItem.id.substring(0, 8)}
                          </Link>
                        </Td>
                        <Td>{caseItem.encounter_id || '-'}</Td>
                        <Td maxW="300px" isTruncated title={caseItem.case_description}>
                          {caseItem.case_description || '-'}
                        </Td>
                        <Td>{caseItem.case_type || '-'}</Td>
                        <Td>
                          {getStatusBadge(caseItem.status || caseItem.cpt_status)}
                        </Td>
                        <Td>{caseItem.date_updated ? new Date(caseItem.date_updated).toLocaleDateString() : '-'}</Td>
                        {isNMA && (
                          <Td>
                            <Link 
                              as={RouterLink} 
                              to={`/ionmform?caseid=${caseItem.external_case_id || caseItem.id}`}
                              color="purple.600"
                              fontSize="sm"
                            >
                              View NMA Case
                            </Link>
                          </Td>
                        )}
                        {isNMA && (
                          <Td>
                            <Link 
                              as={RouterLink} 
                              to={`/claim-analysis/${caseItem.external_case_id || caseItem.id}`}
                              color="teal.600"
                              fontSize="sm"
                              fontWeight="bold"
                            >
                              Analyze Claim
                            </Link>
                          </Td>
                        )}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </CardBody>
        </Card>
      </Box>
    </Box>
  );
};

export default GlobalSearchResults;
